import { DeleteOutlined, UploadOutlined, UserOutlined } from "@ant-design/icons";
import { Avatar, Button, Space, Typography, Upload } from "antd";
import type { RcFile, UploadProps } from "antd/es/upload";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useDeleteEmployeePhoto,
	useEmployeePhotoUrl,
	useSetEmployeePhoto,
} from "../hooks/useEmployees";
import type { Employee } from "../types";

interface EmployeePhotoFieldProps {
	employee: Employee;
}

/** Face AI ham, saqlagich ham qabul qiladigan turlar (`files.storage.ts`). */
const ACCEPT = "image/jpeg,image/png,image/gif";

/**
 * Xodimning profil rasmi (1b-2 spec §10).
 *
 * ⚠️ **Faqat MAVJUD xodim uchun.** Yaratish formasida bu maydon yo'q: Face AI
 * guruhi `employeeId` ustiga quriladi (`group_id = employeeId`), ya'ni xodim
 * yozilmagunicha rasm biriktiriladigan guruh mavjud emas (spec §10).
 *
 * ⚠️ Yuklash forma yuborilishining bir qismi EMAS — u darhol
 * `PATCH /employees/{id}/photo` qiladi. Sabab: enrollment SINXRON va u
 * o'zining 422/503 natijalariga ega; ularni forma saqlash xatosi bilan
 * aralashtirsak, admin «xodim saqlanmadi» deb tushunardi — aslida saqlangan,
 * faqat rasm qabul qilinmagan.
 */
export function EmployeePhotoField({ employee }: EmployeePhotoFieldProps) {
	const { t } = useTranslation("employees");
	// Rasm alohida marshrut orqali o'zgaradi, ya'ni `employee` prop'i
	// (ro'yxatdan kelgan) darhol yangilanmaydi — joriy holat shu yerda
	// kuzatiladi va mutatsiya javobidan yangilanadi.
	const [photoFileId, setPhotoFileId] = useState(employee.photoFileId);

	useEffect(() => {
		setPhotoFileId(employee.photoFileId);
	}, [employee.photoFileId]);

	const photoUrl = useEmployeePhotoUrl(employee.id, photoFileId);
	const setPhoto = useSetEmployeePhoto();
	const deletePhoto = useDeleteEmployeePhoto();

	const isBusy = setPhoto.isPending || deletePhoto.isPending;

	// Antd'ning `action` prop'i ishlatilmaydi — u alohida XHR yuboradi va
	// `apiClient` ning token hamda single-flight refresh interceptorlarini
	// chetlab o'tardi (`FileUpload` dagi bilan bir xil sabab).
	const customRequest: NonNullable<UploadProps["customRequest"]> = async (options) => {
		try {
			const updated = await setPhoto.mutateAsync({
				id: employee.id,
				file: options.file as RcFile,
			});
			setPhotoFileId(updated.photoFileId);
			options.onSuccess?.(updated);
		} catch (error) {
			// Xabar `useSetEmployeePhoto` da ko'rsatiladi (422 — serverning
			// o'z matni, 503 — alohida matn), bu yerda faqat Antd'ga bildiriladi.
			options.onError?.(error as Error);
		}
	};

	const onDelete = async () => {
		try {
			await deletePhoto.mutateAsync(employee.id);
			setPhotoFileId(null);
		} catch {
			// Xabar `useDeleteEmployeePhoto` ning `onError` ida ko'rsatiladi.
			// `mutateAsync` baribir reject qiladi, ushlanmasa esa bu
			// `onClick` ichida unhandled rejection bo'lardi (yuklash yo'lida
			// ham shu sababdan `try/catch` bor).
		}
	};

	return (
		<Space align="start" size="middle">
			<Avatar shape="square" size={96} src={photoUrl} icon={<UserOutlined />} />

			<Space direction="vertical" size="small">
				<Space size="small">
					<Upload
						accept={ACCEPT}
						customRequest={customRequest}
						showUploadList={false}
						maxCount={1}
						disabled={isBusy}
					>
						<Button icon={<UploadOutlined />} loading={setPhoto.isPending}>
							{photoFileId ? t("photoField.replace") : t("photoField.upload")}
						</Button>
					</Upload>

					{photoFileId ? (
						<Button
							danger
							icon={<DeleteOutlined />}
							loading={deletePhoto.isPending}
							disabled={isBusy}
							onClick={onDelete}
						>
							{t("photoField.remove")}
						</Button>
					) : null}
				</Space>

				<Typography.Text type="secondary">{t("photoField.hint")}</Typography.Text>
			</Space>
		</Space>
	);
}
